import { useCallback, useMemo, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { TabItem } from '@kula-ai/components';
import type { StepFormApi } from './types';

export type StepValidationCtaLabelArgs = {
  defaultLabel: string;
  activeStep: string;
  activeIndex: number;
  isLastStep: boolean;
};

export type StepValidationBackCtaLabelArgs = {
  defaultLabel: string;
  activeStep: string;
  activeIndex: number;
  /** Tab `value` of the step you would return to. */
  previousStep: string;
  previousIndex: number;
};

/** Readable label when `TabItem.label` is not a plain string. */
export function tabItemStepLabel(item: TabItem | undefined): string {
  if (!item) {
    return '';
  }
  const { label, value } = item;
  if (typeof label === 'string' || typeof label === 'number') {
    return String(label);
  }
  return String(value);
}

/**
 * Multi-step flow with validate-before-navigate, aligned with `Tabs.Root` `tabItems`.
 *
 * Own **`payloadRef`** in the parent so panels can close over it inside `tabItems`.
 */
export type StepValidationConfig<
  TPayload extends Record<string, Record<string, unknown>>,
> = {
  tabItems: TabItem[];
  payloadRef: MutableRefObject<TPayload>;
  stepRefs: Record<string, RefObject<StepFormApi | null>>;
  onComplete?: (_payload: TPayload) => void | Promise<void>;
  finalActionLabel?: string;
  /** Shown before the *next* tab’s title, e.g. `Next:` → `Next: Communication`. */
  nextPrefix?: string;
  /** Shown before the *previous* tab’s title, e.g. `Back:` → `Back: Details`. */
  backPrefix?: string;
  enableBack?: boolean;
  /**
   * Optional primary CTA override: fixed string, or function receiving the default
   * label plus step context.
   */
  ctaLabel?: string | ((_args: StepValidationCtaLabelArgs) => string);
  /**
   * Optional back CTA override: fixed string, or function receiving the default
   * (`Back: PreviousName`) plus step context.
   */
  backCtaLabel?: string | ((_args: StepValidationBackCtaLabelArgs) => string);
  /**
   * Last-step primary action (`goNextOrComplete` on the final tab):
   * - `'activeStepOnly'` — validate + sync **only** the current step, then `onComplete` (default; backward compatible).
   * - `'validateAllSteps'` — validate + sync **every** step in order (full wizard flush before save).
   */
  wizardCompleteMode?: 'activeStepOnly' | 'validateAllSteps';
  /**
   * When `wizardCompleteMode` is `'validateAllSteps'` and a step fails, activate that tab so errors are visible.
   * Default `true`. Ignored for `'activeStepOnly'`.
   */
  activateFirstInvalidStepOnWizardComplete?: boolean;
  /**
   * Optional callback to expose why progression/save was blocked.
   * Useful for debug logs and user-visible messaging in parent flows.
   */
  onValidationBlocked?: (_args: {
    failedStep?: string;
    activeStep: string;
    targetStep?: string;
    isFinalAction: boolean;
  }) => void;
};

export type StepValidationResult<TPayload> = {
  payloadRef: MutableRefObject<TPayload>;
  tabItems: TabItem[];
  activeStep: string;
  isBusy: boolean;
  onStepChange: (_next: string) => void;
  goNextOrComplete: () => Promise<void>;
  goBack: () => Promise<void>;
  canGoBack: boolean;
  primaryButtonLabel: string;
  /** Set when `enableBack` and `canGoBack` (e.g. `Back: Details`). */
  backButtonLabel: string | undefined;
};

export const useStepValidation = <
  TPayload extends Record<string, Record<string, unknown>>,
>(
  config: StepValidationConfig<TPayload>
): StepValidationResult<TPayload> => {
  const {
    tabItems: configTabItems,
    payloadRef,
    stepRefs,
    onComplete,
    finalActionLabel = 'Save',
    nextPrefix = 'Next:',
    backPrefix = 'Back:',
    enableBack = false,
    ctaLabel,
    backCtaLabel,
    wizardCompleteMode = 'activeStepOnly',
    activateFirstInvalidStepOnWizardComplete = true,
    onValidationBlocked,
  } = config;

  const steps = useMemo(
    () => configTabItems.map((item) => String(item.value)),
    [configTabItems]
  );

  const [activeStep, setActiveStep] = useState<string>(() =>
    configTabItems[0] ? String(configTabItems[0].value) : ''
  );
  const [isBusy, setIsBusy] = useState(false);
  const lastFailedStepRef = useRef<string | undefined>(undefined);

  const stepIndex = useCallback(
    (value: string) => steps.indexOf(value),
    [steps]
  );

  const validateAndSyncRange = useCallback(
    async (
      fromInclusive: number,
      toExclusive: number,
      opts?: { setStepOnFailure?: boolean }
    ) => {
      lastFailedStepRef.current = undefined;
      for (let i = fromInclusive; i < toExclusive; i++) {
        const key = steps[i];
        const api = stepRefs[key]?.current;
        if (!api) {
          continue;
        }
        const ok = await api.validate();
        if (!ok) {
          lastFailedStepRef.current = key;
          if (opts?.setStepOnFailure) {
            setActiveStep(steps[i]);
          }
          return false;
        }
        api.syncToPayload();
      }
      return true;
    },
    [setActiveStep, stepRefs, steps]
  );

  const tryGoToStep = useCallback(
    async (next: string) => {
      const curIdx = stepIndex(activeStep);
      const nextIdx = stepIndex(next);
      if (nextIdx < 0 || nextIdx === curIdx) {
        return;
      }

      setIsBusy(true);
      try {
        if (nextIdx > curIdx) {
          const ok = await validateAndSyncRange(curIdx, nextIdx, {
            setStepOnFailure: true,
          });
          if (!ok) {
            onValidationBlocked?.({
              failedStep: lastFailedStepRef.current,
              activeStep,
              targetStep: next,
              isFinalAction: false,
            });
            return;
          }
        } else {
          /** Going backward (stepper): persist draft without blocking on validation. */
          const curKey = steps[curIdx];
          stepRefs[curKey]?.current?.syncToPayload();
        }
        setActiveStep(next);
      } finally {
        setIsBusy(false);
      }
    },
    [
      activeStep,
      onValidationBlocked,
      stepIndex,
      stepRefs,
      steps,
      validateAndSyncRange,
    ]
  );

  const onStepChange = useCallback(
    (next: string) => {
      if (!steps.includes(next)) {
        return;
      }
      void tryGoToStep(next);
    },
    [steps, tryGoToStep]
  );

  const goBack = useCallback(async () => {
    const curIdx = steps.indexOf(activeStep);
    if (!enableBack || curIdx <= 0) {
      return;
    }

    setIsBusy(true);
    try {
      /** Back footer CTA: always allow leaving the step; save draft only. */
      const curKey = steps[curIdx];
      stepRefs[curKey]?.current?.syncToPayload();
      setActiveStep(steps[curIdx - 1]);
    } finally {
      setIsBusy(false);
    }
  }, [activeStep, enableBack, stepRefs, steps]);

  const goNextOrComplete = useCallback(async () => {
    const curIdx = steps.indexOf(activeStep);
    if (curIdx < 0) {
      onValidationBlocked?.({
        activeStep,
        isFinalAction: false,
      });
      return;
    }

    if (curIdx === steps.length - 1) {
      setIsBusy(true);
      try {
        if (wizardCompleteMode === 'validateAllSteps') {
          const ok = await validateAndSyncRange(0, steps.length, {
            setStepOnFailure: activateFirstInvalidStepOnWizardComplete,
          });
          if (!ok) {
            onValidationBlocked?.({
              failedStep: lastFailedStepRef.current,
              activeStep,
              isFinalAction: true,
            });
            return;
          }
        } else {
          const key = steps[curIdx];
          const api = stepRefs[key]?.current;
          if (api) {
            const ok = await api.validate();
            if (!ok) {
              lastFailedStepRef.current = key;
              onValidationBlocked?.({
                failedStep: key,
                activeStep,
                isFinalAction: true,
              });
              return;
            }
            api.syncToPayload();
          }
        }
        await onComplete?.(payloadRef.current);
      } finally {
        setIsBusy(false);
      }
      return;
    }

    setIsBusy(true);
    try {
      const ok = await validateAndSyncRange(curIdx, curIdx + 1);
      if (ok) {
        setActiveStep(steps[curIdx + 1]);
      } else {
        onValidationBlocked?.({
          failedStep: lastFailedStepRef.current,
          activeStep,
          targetStep: steps[curIdx + 1],
          isFinalAction: false,
        });
      }
    } finally {
      setIsBusy(false);
    }
  }, [
    activateFirstInvalidStepOnWizardComplete,
    activeStep,
    onComplete,
    payloadRef,
    stepRefs,
    steps,
    validateAndSyncRange,
    wizardCompleteMode,
    onValidationBlocked,
  ]);

  const activeIndex = stepIndex(activeStep);
  const isLastStep = activeIndex === steps.length - 1;

  const resolvedTabItems = useMemo(
    () =>
      configTabItems.map((item) => ({
        ...item,
        disabled: Boolean(isBusy) || Boolean(item.disabled),
      })),
    [configTabItems, isBusy]
  );

  const defaultPrimaryLabel = useMemo(() => {
    if (isLastStep) {
      return finalActionLabel;
    }
    const nextItem = configTabItems[activeIndex + 1];
    const nextName = tabItemStepLabel(nextItem);
    return `${nextPrefix} ${nextName}`;
  }, [activeIndex, configTabItems, finalActionLabel, isLastStep, nextPrefix]);

  const primaryButtonLabel = useMemo(() => {
    if (typeof ctaLabel === 'string') {
      return ctaLabel;
    }
    if (typeof ctaLabel === 'function') {
      return ctaLabel({
        defaultLabel: defaultPrimaryLabel,
        activeStep,
        activeIndex,
        isLastStep,
      });
    }
    return defaultPrimaryLabel;
  }, [activeIndex, activeStep, ctaLabel, defaultPrimaryLabel, isLastStep]);

  const canGoBack = enableBack && activeIndex > 0;

  const defaultBackLabel = useMemo(() => {
    if (!canGoBack) {
      return '';
    }
    const prevItem = configTabItems[activeIndex - 1];
    const prevName = tabItemStepLabel(prevItem);
    return `${backPrefix} ${prevName}`;
  }, [activeIndex, backPrefix, canGoBack, configTabItems]);

  const backButtonLabel = useMemo(() => {
    if (!enableBack || !canGoBack) {
      return undefined;
    }
    if (typeof backCtaLabel === 'string') {
      return backCtaLabel;
    }
    if (typeof backCtaLabel === 'function') {
      const previousIndex = activeIndex - 1;
      return backCtaLabel({
        defaultLabel: defaultBackLabel,
        activeStep,
        activeIndex,
        previousStep: steps[previousIndex],
        previousIndex,
      });
    }
    return defaultBackLabel;
  }, [
    activeIndex,
    activeStep,
    backCtaLabel,
    canGoBack,
    defaultBackLabel,
    enableBack,
    steps,
  ]);

  return useMemo(
    () => ({
      payloadRef,
      tabItems: resolvedTabItems,
      activeStep,
      isBusy,
      onStepChange,
      goNextOrComplete,
      goBack,
      canGoBack,
      primaryButtonLabel,
      backButtonLabel,
    }),
    [
      activeStep,
      backButtonLabel,
      canGoBack,
      goBack,
      goNextOrComplete,
      isBusy,
      onStepChange,
      payloadRef,
      primaryButtonLabel,
      resolvedTabItems,
    ]
  );
};
